import type { PublishedCollection } from '@/types/schema';
import { AppApiClient } from '@/api/client';
import type { AppListing, AppMember, AppInvite, AppRole, BackendClient, InviteInput, PlatformBackend, PublishInput, WriteMeta } from './types';

export interface AwsBackendConfig {
  baseUrl: string;
  getToken: () => string | null;
}

async function request<T>(url: string, method: string, token: string | null, body?: unknown, siteToken?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (siteToken) headers['X-Site-Token'] = siteToken;
  const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json();
  if (!res.ok) throw new Error((data as { error?: string })?.error ?? `Request failed (${res.status})`);
  return data as T;
}

/** AWS-backed implementation of the plan-1 contracts. */
export class AwsBackend implements PlatformBackend {
  constructor(private config: AwsBackendConfig) {}

  getClient(appId: string): BackendClient {
    const client = new AppApiClient({ baseUrl: this.config.baseUrl, appId });
    client.setAuthToken(this.config.getToken());
    return {
      listRecords: (entity, opts) => client.listRecords(entity, opts) as ReturnType<BackendClient['listRecords']>,
      getRecord: (entity, id) => client.getRecord(entity, id) as ReturnType<BackendClient['getRecord']>,
      createRecord: (entity, data, meta?: WriteMeta) => client.createRecord(entity, data, meta) as ReturnType<BackendClient['createRecord']>,
      updateRecord: (entity, id, data, meta?: WriteMeta) => client.updateRecord(entity, id, data, meta) as ReturnType<BackendClient['updateRecord']>,
      deleteRecord: (entity, id, meta?: WriteMeta) => client.deleteRecord(entity, id, meta),
    };
  }

  get platform(): PlatformBackend {
    return this;
  }

  publishApp(input: PublishInput): Promise<AppListing> {
    return request<AppListing>(`${this.config.baseUrl}/apps`, 'POST', this.config.getToken(), input);
  }

  async listApps(): Promise<AppListing[]> {
    const res = await request<{ items: AppListing[] }>(`${this.config.baseUrl}/apps`, 'GET', this.config.getToken());
    return res.items;
  }

  async getApp(appId: string): Promise<AppListing | null> {
    try {
      return await request<AppListing>(`${this.config.baseUrl}/apps/${appId}`, 'GET', this.config.getToken());
    } catch {
      return null;
    }
  }

  enroll(appId: string): Promise<{ appId: string; enrolled: boolean }> {
    return request(`${this.config.baseUrl}/apps/${appId}/enroll`, 'POST', this.config.getToken());
  }

  async listMyApps(): Promise<AppListing[]> {
    const res = await request<{ items: AppListing[] }>(`${this.config.baseUrl}/me/apps`, 'GET', this.config.getToken());
    return res.items;
  }

  unpublish(appId: string): Promise<{ appId: string; unpublished: boolean }> {
    return request(`${this.config.baseUrl}/apps/${appId}`, 'DELETE', this.config.getToken());
  }

  reportApp(appId: string, reason: string): Promise<{ reported: boolean }> {
    return request(`${this.config.baseUrl}/apps/${appId}/report`, 'POST', this.config.getToken(), { reason });
  }

  /** Purge the signed-in user's server data (called before deleting the auth account). */
  deleteAccount(): Promise<{ deleted: number }> {
    return request(`${this.config.baseUrl}/me/account`, 'DELETE', this.config.getToken());
  }

  // --- Email subscribers ---
  /** Public: collect an email (server sends the double-opt-in confirmation). */
  subscribe(appId: string, email: string): Promise<{ status: string }> {
    return request(`${this.config.baseUrl}/apps/${appId}/subscribe`, 'POST', this.config.getToken(), { email });
  }

  /** Owner-only: send an update to confirmed subscribers (unsubscribe + footer added server-side). */
  broadcast(appId: string, payload: { subject: string; body: string; senderAddress: string }): Promise<{ recipients: number; sent: number }> {
    return request(`${this.config.baseUrl}/apps/${appId}/broadcast`, 'POST', this.config.getToken(), payload);
  }

  // --- Publish to website ---

  publishToWeb(appId: string, opts: { visibility: 'public' | 'password'; password?: string; html?: string; aiAccess?: 'creator' | 'users' | 'open'; aiMonthlyBudget?: number; aiDailyPerPerson?: number; schemas?: PublishedCollection[] }): Promise<{ webPublished: boolean; webVisibility: 'public' | 'password'; slug?: string; siteUrl?: string; htmlHosted?: boolean }> {
    return request(`${this.config.baseUrl}/apps/${appId}/web`, 'POST', this.config.getToken(), opts);
  }

  unpublishFromWeb(appId: string): Promise<{ webPublished: boolean }> {
    return request(`${this.config.baseUrl}/apps/${appId}/web`, 'DELETE', this.config.getToken());
  }

  async unlockSite(appId: string, password: string): Promise<{ token: string } | null> {
    try {
      return await request<{ token: string }>(`${this.config.baseUrl}/sites/${appId}/unlock`, 'POST', null, { password });
    } catch {
      return null;
    }
  }

  async getAppWithSite(appId: string, siteToken?: string): Promise<AppListing | null> {
    try {
      return await request<AppListing>(`${this.config.baseUrl}/apps/${appId}`, 'GET', this.config.getToken(), undefined, siteToken);
    } catch {
      return null;
    }
  }

  // --- In-app users (membership + invites) ---

  inviteToApp(appId: string, input: InviteInput): Promise<AppInvite> {
    return request(`${this.config.baseUrl}/apps/${appId}/invites`, 'POST', this.config.getToken(), input);
  }

  acceptInvite(token: string): Promise<{ appId: string; role: AppRole }> {
    return request(`${this.config.baseUrl}/invites/${token}/accept`, 'POST', this.config.getToken());
  }

  async listMembers(appId: string): Promise<AppMember[]> {
    const res = await request<{ items: AppMember[] }>(`${this.config.baseUrl}/apps/${appId}/members`, 'GET', this.config.getToken());
    return res.items;
  }

  setMemberRole(appId: string, userId: string, role: AppRole): Promise<AppMember> {
    return request(`${this.config.baseUrl}/apps/${appId}/members/${userId}`, 'PUT', this.config.getToken(), { role });
  }

  removeMember(appId: string, userId: string): Promise<{ appId: string; userId: string; removed: boolean }> {
    return request(`${this.config.baseUrl}/apps/${appId}/members/${userId}`, 'DELETE', this.config.getToken());
  }
}
